import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, MenuItem, Chip, Collapse,
  InputAdornment, IconButton,
} from "@mui/material";
import ExpandMoreIcon       from "@mui/icons-material/ExpandMore";
import ExpandLessIcon       from "@mui/icons-material/ExpandLess";
import SearchIcon           from "@mui/icons-material/Search";
import FilterListIcon       from "@mui/icons-material/FilterList";
import LocationOnIcon       from "@mui/icons-material/LocationOn";
import TwoWheelerIcon       from "@mui/icons-material/TwoWheeler";
import WarningAmberIcon     from "@mui/icons-material/WarningAmber";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import AddIcon              from "@mui/icons-material/Add";
import VisibilityIcon       from "@mui/icons-material/Visibility";

import { soldBikeService } from "../service/SoldBikeService";
import BikeDetailModal         from "../component/Bikedetailmodal";
import DuePaymentModal      from "../component/Duepaymentmodal";
import ExportExcelSell      from "../component/ExportexcelSell";
import ExportPdfSell        from "../component/ExportpdfSell";

/* ─── constants ──────────────────────────────────────────────── */
const LOCATIONS = ["Boroda", "Chaitanyapur", "Mahisadal"];

const LOC_COLOR = {
  Boroda:       { bg: "#e8f4fd", border: "#1976d2", badge: "#1976d2", light: "#bbdefb" },
  Chaitanyapur: { bg: "#f3e5f5", border: "#7b1fa2", badge: "#7b1fa2", light: "#e1bee7" },
  Mahisadal:    { bg: "#e8f5e9", border: "#2e7d32", badge: "#2e7d32", light: "#c8e6c9" },
  Other:        { bg: "#fff8e1", border: "#f57f17", badge: "#f57f17", light: "#ffecb3" },
};

/* ─── helpers ────────────────────────────────────────────────── */
const fmtMoney = (n) =>
  n !== undefined && n !== null && n !== "" && !isNaN(n)
    ? "₹" + Number(n).toLocaleString("en-IN")
    : "—";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/* ─── payment info cell ──────────────────────────────────────── */
const PaymentCell = ({ bike }) => {
  const t = bike?.saleType || "";
  const rows = [];

  if (["CASH", "CASH+EX"].includes(t) || ((["FINANCE", "EX+FIN"].includes(t)) && bike?.cashAmount > 0)) {
    rows.push(
      <Box key="cash" display="flex" alignItems="center" gap={0.5}>
        <Chip label="CASH" size="small"
          sx={{ fontSize: "0.6rem", fontWeight: 700, bgcolor: "#e8f5e9", color: "#2e7d32", height: 18 }} />
        <Typography variant="caption" fontWeight={700} color="#2e7d32">{fmtMoney(bike?.cashAmount)}</Typography>
      </Box>
    );
  }
  if (["FINANCE", "EX+FIN"].includes(t)) {
    rows.push(
      <Box key="fin" display="flex" flexDirection="column">
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip label="FIN" size="small"
            sx={{ fontSize: "0.6rem", fontWeight: 700, bgcolor: "#e3f2fd", color: "#1565c0", height: 18 }} />
          <Typography variant="caption" fontWeight={700} color="#1565c0" noWrap>
            {bike?.finance?.companyName || "—"}
          </Typography>
        </Box>
        <Typography variant="caption" color="#1565c0" sx={{ pl: 0.5 }}>
          {fmtMoney(bike?.finance?.amount)}
        </Typography>
      </Box>
    );
  }
  if (["EXCHANGE", "EX+FIN", "CASH+EX"].includes(t)) {
    rows.push(
      <Box key="ex" display="flex" alignItems="center" gap={0.5}>
        <Chip label="EX" size="small"
          sx={{ fontSize: "0.6rem", fontWeight: 700, bgcolor: "#fff3e0", color: "#e65100", height: 18 }} />
        <Typography variant="caption" fontWeight={700} color="#e65100">
          {fmtMoney(bike?.exchange?.exchangePrice)}
        </Typography>
      </Box>
    );
  }
  if (!rows.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return <Box display="flex" flexDirection="column" gap={0.4}>{rows}</Box>;
};

/* ═══════════════════════════════════════════════════════════════
 *  MAIN COMPONENT
 * ═══════════════════════════════════════════════════════════════ */
const SoldBikeAdminView = () => {

  const [data,        setData]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [expandedLoc, setExpandedLoc] = useState({});

  /* ── view dialog (uses ViewSoldBike) ────────────────────────── */
  const [viewOpen,      setViewOpen]      = useState(false);
  const [selectedBike,  setSelectedBike]  = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  /* ── due payment dialog ────────────────────────────────────── */
  const [dueOpen,      setDueOpen]      = useState(false);
  const [dueSale,      setDueSale]      = useState(null);
  const [dueForm,      setDueForm]      = useState({ amount: "", date: new Date().toISOString().split("T")[0], note: "" });
  const [dueSubmitting,setDueSubmitting]= useState(false);
  const [dueError,     setDueError]     = useState("");
  const [dueSuccess,   setDueSuccess]   = useState("");

  /* ── filters ────────────────────────────────────────────────── */
  const [filters, setFilters] = useState({
    model: "", location: "", customerName: "", mobile: "",
    saleType: "", paymentStatus: "", engineNumber: "", chasisNumber: "",
    fromDate: "", toDate: "", month: "", year: "", search: "",
    financeCompany: "",
  });

  /* ── fetch ──────────────────────────────────────────────────── */
  const fetchData = async (f = filters) => {
    try {
      setLoading(true); setError("");
      const res   = await soldBikeService.getFilteredSoldBikes(f);
      const bikes = res?.data || [];
      setData(bikes);
      const exp = {};
      bikes.forEach((b) => { exp[b.location || "Other"] = true; });
      setExpandedLoc(exp);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleChange = (e) =>
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  const resetFilters = () => {
    const empty = {
      model: "", location: "", customerName: "", mobile: "",
      saleType: "", paymentStatus: "", engineNumber: "", chasisNumber: "",
      fromDate: "", toDate: "", month: "", year: "", search: "",
      financeCompany: "",
    };
    setFilters(empty);
    fetchData(empty);
  };

  /* ── group by location ──────────────────────────────────────── */
  const grouped = data.reduce((acc, b) => {
    const loc = b.location || "Other";
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(b);
    return acc;
  }, {});

  const toggleLoc = (loc) =>
    setExpandedLoc((p) => ({ ...p, [loc]: !p[loc] }));

  /* ── view ── uses soldBikeService.getSpecificBike like file 2 ── */
  const handleView = async (id) => {
    try {
      setViewOpen(true);
      setDetailLoading(true);
      setSelectedBike(null);
      const res = await soldBikeService.getSpecificBike(id);
      setSelectedBike(res?.data || null);
    } catch {
      setError("Failed to load details");
    } finally {
      setDetailLoading(false);
    }
  };

  /* ── due — identical to file 2 DuePaymentModal logic ─────────── */
  const openDueModal = (bike) => {
    setDueSale(bike);
    setDueForm({ amount: "", date: new Date().toISOString().split("T")[0], note: "" });
    setDueError("");
    setDueSuccess("");
    setDueOpen(true);
  };

  const handleDueSubmit = async () => {
    if (!dueForm.amount || Number(dueForm.amount) <= 0) {
      setDueError("Please enter a valid amount");
      return;
    }
    if (Number(dueForm.amount) > (dueSale?.due?.remainingAmount || 0)) {
      setDueError(`Max payable: ₹${dueSale?.due?.remainingAmount}`);
      return;
    }
    try {
      setDueSubmitting(true);
      setDueError("");
      await soldBikeService.addPayment(dueSale._id, {
        amount: Number(dueForm.amount),
        date:   dueForm.date,
        note:   dueForm.note,
      });
      setDueSuccess("Payment recorded successfully!");
      setTimeout(() => { setDueOpen(false); fetchData(); }, 1200);
    } catch (err) {
      setDueError(err?.response?.data?.message || "Payment failed");
    } finally {
      setDueSubmitting(false);
    }
  };

  /* ── finance company options from loaded data ───────────────── */
  const financeOptions = [
    ...new Set(
      data
        .filter((b) => b?.finance?.companyName)
        .map((b) => b.finance.companyName.trim())
    )
  ].sort();

  /* ══════════════════════════════════════════════════════════════
   *  RENDER
   * ══════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>

      {/* ── HEADER ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ bgcolor: "#1565c0", borderRadius: 2, p: 1, display: "flex" }}>
            <TwoWheelerIcon sx={{ color: "#fff", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0d1b2a" letterSpacing={-0.5}>
              Sold Bikes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.length} total records · Showroom-wise view
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <ExportExcelSell data={data} filters={filters} />
          <ExportPdfSell   data={data} filters={filters} />
          <Button
            startIcon={<FilterListIcon />}
            variant={filtersOpen ? "contained" : "outlined"}
            size="small"
            onClick={() => setFiltersOpen((p) => !p)}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: "0.75rem" }}>
            {filtersOpen ? "Hide Filters" : "Show Filters"}
          </Button>
        </Box>
      </Box>

      {/* ── FILTERS ── */}
      <Collapse in={filtersOpen}>
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: "1px solid #e0e7ef", borderRadius: 3 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={2}>
              <TextField label="Model" name="model" fullWidth size="small"
                value={filters.model} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Location" name="location" fullWidth size="small"
                value={filters.location} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Customer" name="customerName" fullWidth size="small"
                value={filters.customerName} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Mobile" name="mobile" fullWidth size="small"
                value={filters.mobile} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Sale Type" name="saleType" fullWidth size="small"
                value={filters.saleType} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CASH">CASH</MenuItem>
                <MenuItem value="FINANCE">FINANCE</MenuItem>
                <MenuItem value="EXCHANGE">EXCHANGE</MenuItem>
                <MenuItem value="EX+FIN">EX+FIN</MenuItem>
                <MenuItem value="CASH+EX">CASH+EX</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Due Status" name="paymentStatus" fullWidth size="small"
                value={filters.paymentStatus} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PAID">PAID</MenuItem>
                <MenuItem value="DUE">DUE</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Finance Company" name="financeCompany" fullWidth size="small"
                value={filters.financeCompany} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {financeOptions.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Engine No" name="engineNumber" fullWidth size="small"
                value={filters.engineNumber} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Serial No" name="chasisNumber" fullWidth size="small"
                value={filters.chasisNumber} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="From Date" name="fromDate" fullWidth size="small"
                InputLabelProps={{ shrink: true }} value={filters.fromDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="To Date" name="toDate" fullWidth size="small"
                InputLabelProps={{ shrink: true }} value={filters.toDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Month" name="month" fullWidth size="small"
                value={filters.month} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {[...Array(12)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString("default", { month: "long" })}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="number" label="Year" name="year" fullWidth size="small"
                value={filters.year} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Search" name="search" fullWidth size="small"
                value={filters.search} onChange={handleChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Button fullWidth variant="contained" onClick={() => fetchData()}
                sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#1565c0" }}>
                Apply Filter
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button fullWidth variant="outlined" onClick={resetFilters}
                sx={{ borderRadius: 2, fontWeight: 700 }}>
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {loading && <Box textAlign="center" py={6}><CircularProgress /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && data.length === 0 && (
        <Alert severity="info">No sold bikes found for the selected filters.</Alert>
      )}

      {/* ── LOCATION GROUPED TABLES ── */}
      {!loading && Object.entries(grouped).map(([location, bikes]) => {
        const theme    = LOC_COLOR[location] || LOC_COLOR.Other;
        const isOpen   = expandedLoc[location] !== false;
        const dueCount  = bikes.filter((b) => b?.due?.status === "DUE").length;
        const paidCount = bikes.filter((b) => b?.due?.status === "PAID").length;
        const totalRev  = bikes.reduce((s, b) => s + (b.salePrice || 0), 0);
        const totalOrp  = bikes.reduce((s, b) => s + (b.orp || 0), 0);

        const financeCompanies = [
          ...new Set(
            bikes
              .filter((b) => b?.finance?.companyName)
              .map((b) => b.finance.companyName.trim().split(/\s+/)[0])
          )
        ];

        return (
          <Box key={location} mb={3}>

            {/* Location header */}
            <Paper elevation={0} onClick={() => toggleLoc(location)} sx={{
              px: 2.5, py: 1.5,
              background: `linear-gradient(135deg, ${theme.bg} 0%, #fff 100%)`,
              border: `2px solid ${theme.border}`,
              borderBottom: isOpen ? "none" : `2px solid ${theme.border}`,
              borderRadius: isOpen ? "12px 12px 0 0" : "12px",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 1, transition: "all 0.2s",
            }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <LocationOnIcon sx={{ color: theme.badge, fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight={800} color={theme.badge} letterSpacing={0.5}>
                  {location}
                </Typography>
                <Chip label={`${bikes.length} bikes`} size="small"
                  sx={{ bgcolor: theme.badge, color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
              </Box>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                {financeCompanies.map((name) => (
                  <Chip key={name} label={name} size="small"
                    sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 700, fontSize: "0.65rem", border: "1px solid #90caf9", height: 20 }} />
                ))}
                {dueCount > 0 && (
                  <Chip icon={<WarningAmberIcon sx={{ fontSize: 14 }} />} label={`${dueCount} DUE`} size="small"
                    sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 700, border: "1px solid #ffb74d", fontSize: "0.7rem" }} />
                )}
                <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={`${paidCount} PAID`} size="small"
                  sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 700, border: "1px solid #a5d6a7", fontSize: "0.7rem" }} />
                <Typography variant="body2" fontWeight={700} color={theme.badge}>
                  Sale: {fmtMoney(totalRev)} &nbsp;|&nbsp; ORP: {fmtMoney(totalOrp)}
                </Typography>
                {isOpen ? <ExpandLessIcon sx={{ color: theme.badge }} /> : <ExpandMoreIcon sx={{ color: theme.badge }} />}
              </Box>
            </Paper>

            {/* Table */}
            <Collapse in={isOpen}>
              <Paper elevation={0} sx={{
                border: `2px solid ${theme.border}`, borderTop: "none",
                borderRadius: "0 0 12px 12px", overflow: "hidden",
              }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.light }}>
                        {[
                          "#", "Invoice", "Sale Date", "Model", "Customer",
                          "EXShowroom", "ORP", "Sale Type", "Payment Info",
                          "Due Status", "Remaining", "Actions",
                        ].map((h) => (
                          <TableCell key={h} sx={{
                            fontWeight: 800, fontSize: "0.7rem", color: theme.badge,
                            whiteSpace: "nowrap", py: 1.2, letterSpacing: 0.3,
                          }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bikes.map((b, idx) => {
                        const isDue = b?.due?.status === "DUE";
                        return (
                          <TableRow key={b._id} sx={{
                            bgcolor: isDue ? "#fff8f5" : idx % 2 === 0 ? "#fff" : "#fafbff",
                            "&:hover": { bgcolor: isDue ? "#fff0eb" : "#f0f4ff" },
                            transition: "background 0.15s",
                          }}>
                            <TableCell sx={{ color: "#999", fontSize: "0.72rem", fontWeight: 600 }}>{idx + 1}</TableCell>

                            <TableCell sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1565c0", whiteSpace: "nowrap" }}>
                              {b.invoiceNo || "—"}
                            </TableCell>

                            <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap", color: "#555" }}>
                              {fmtDate(b.saleDate)}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" fontWeight={700} fontSize={12}>{b.model || "—"}</Typography>
                              {b.color && <Typography variant="caption" color="text.secondary">{b.color}</Typography>}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" fontWeight={700} fontSize={12}>{b.customerName || "—"}</Typography>
                              {b.fatherName && <Typography variant="caption" color="text.secondary">S/O {b.fatherName}</Typography>}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", fontSize: 12 }}>
                              {fmtMoney(b.salePrice)}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", fontSize: 12 }}>
                              {fmtMoney(b.orp)}
                            </TableCell>

                            <TableCell>
                              <Chip label={b.saleType || "—"} size="small" sx={{
                                fontSize: "0.62rem", fontWeight: 700,
                                bgcolor: b.saleType === "CASH" ? "#e8f5e9"
                                  : b.saleType === "FINANCE" ? "#e3f2fd"
                                  : b.saleType?.includes("EX") ? "#fff3e0" : "#f5f5f5",
                                color: b.saleType === "CASH" ? "#2e7d32"
                                  : b.saleType === "FINANCE" ? "#1565c0"
                                  : b.saleType?.includes("EX") ? "#e65100" : "#555",
                              }} />
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <PaymentCell bike={b} />
                            </TableCell>

                            <TableCell>
                              <Chip
                                icon={isDue
                                  ? <WarningAmberIcon sx={{ fontSize: 12 }} />
                                  : <CheckCircleIcon  sx={{ fontSize: 12 }} />}
                                label={b?.due?.status || "PAID"} size="small"
                                sx={{
                                  fontSize: "0.62rem", fontWeight: 700,
                                  bgcolor: isDue ? "#fff3e0" : "#e8f5e9",
                                  color:   isDue ? "#e65100" : "#2e7d32",
                                  border:  `1px solid ${isDue ? "#ffb74d" : "#a5d6a7"}`,
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700, color: isDue ? "#e65100" : "#2e7d32", whiteSpace: "nowrap", fontSize: 12 }}>
                              {isDue ? fmtMoney(b?.due?.remainingAmount) : "—"}
                            </TableCell>

                            <TableCell>
                              <Box display="flex" gap={0.75} flexWrap="nowrap">
                                <Button size="small" variant="outlined"
                                  startIcon={<VisibilityIcon sx={{ fontSize: 12 }} />}
                                  onClick={() => handleView(b._id)}
                                  sx={{ fontSize: "0.68rem", fontWeight: 700, borderRadius: 1.5, px: 1, py: 0.3, minWidth: 0 }}>
                                  View
                                </Button>
                                {isDue && (
                                  <Button size="small" variant="contained"
                                    startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                    onClick={() => openDueModal(b)}
                                    sx={{ fontSize: "0.68rem", fontWeight: 700, borderRadius: 1.5, px: 1, py: 0.3, minWidth: 0, bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" } }}>
                                    Due
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </Collapse>
          </Box>
        );
      })}

      {/* ── VIEW DIALOG — separate file ViewSoldBike ────────────── */}
      <BikeDetailModal
        open={viewOpen}
        onClose={() => { setViewOpen(false); setSelectedBike(null); }}
        selectedBike={selectedBike}
        detailLoading={detailLoading}
      />

      {/* ── DUE PAYMENT MODAL ───────────────────────────────────── */}
      <DuePaymentModal
        open={dueOpen}
        onClose={() => setDueOpen(false)}
        dueSale={dueSale}
        dueForm={dueForm}
        setDueForm={setDueForm}
        dueError={dueError}
        dueSuccess={dueSuccess}
        dueSubmitting={dueSubmitting}
        onSubmit={handleDueSubmit}
      />

    </Box>
  );
};

export default SoldBikeAdminView;